import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTournament, AllPlayer, PlayerMatchRecord } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import ConfirmModal from '../components/ConfirmModal';
import { resizeImageToDataUrl } from '../utils/resizeImage';
import { useSocketEvents, useConnectionStatus } from '../adapters/webSocket';

const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"%3E%3Ccircle cx="32" cy="32" r="32" fill="%23236038"/%3E%3Ccircle cx="32" cy="24" r="10" fill="%23c9a84c" opacity="0.7"/%3E%3Cellipse cx="32" cy="52" rx="16" ry="10" fill="%23c9a84c" opacity="0.7"/%3E%3C/svg%3E';

interface EditingState  { playerId: string; preview: string | null; }
interface HandicapEdit  { playerId: string; value: string; }
interface NameEdit      { playerId: string; value: string; }

// ─── Match history drawer ─────────────────────────────────────────────────────

interface DrawerState {
  player: AllPlayer;
  filter: 'win' | 'loss';
}

function MatchHistoryDrawer({
  state,
  onClose,
  fetchPlayerMatches,
  onNavigateToTournament,
}: {
  state: DrawerState;
  onClose: () => void;
  fetchPlayerMatches: (id: string) => Promise<PlayerMatchRecord[]>;
  onNavigateToTournament: (id: string) => void;
}) {
  const [records, setRecords] = useState<PlayerMatchRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError  ] = useState<string | null>(null);
  const [filter,  setFilter ] = useState<'win' | 'loss'>(state.filter);

  useEffect(() => {
    setLoading(true);
    fetchPlayerMatches(state.player._id)
      .then(setRecords)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load matches'))
      .finally(() => setLoading(false));
  }, [state.player._id, fetchPlayerMatches]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const shown = (records ?? []).filter(r => r.result === filter);

  const pic = state.player.profilePictureUrl ?? PLACEHOLDER;

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000,
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  };

  const sheet: React.CSSProperties = {
    background: 'linear-gradient(180deg,#1a2e1f 0%,#111 100%)',
    border: '1px solid rgba(201,168,76,0.2)',
    borderRadius: '16px 16px 0 0',
    width: '100%', maxWidth: '640px',
    maxHeight: '82vh',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={sheet}>

        {/* Header */}
        <div style={{ padding: '1rem 1rem 0.5rem', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <img
              src={pic}
              alt={state.player.name}
              style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(201,168,76,0.35)', flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {state.player.name}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginTop: '0.1rem' }}>
                {state.player.wins}W · {state.player.losses}L
                {(state.player.wins + state.player.losses) > 0 && (
                  <> · {Math.round((state.player.wins / (state.player.wins + state.player.losses)) * 100)}% win rate</>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: 'var(--chalk-dim)', fontSize: '1rem', width: 32, height: 32, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >✕</button>
          </div>

          {/* Win / Loss toggle */}
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {(['win', 'loss'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '0.35rem 1rem', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700,
                  cursor: 'pointer', transition: 'all 0.15s',
                  background: filter === f
                    ? (f === 'win' ? 'rgba(46,204,113,0.2)' : 'rgba(231,76,60,0.2)')
                    : 'rgba(255,255,255,0.04)',
                  border: filter === f
                    ? (f === 'win' ? '1px solid rgba(46,204,113,0.5)' : '1px solid rgba(231,76,60,0.5)')
                    : '1px solid rgba(255,255,255,0.1)',
                  color: filter === f
                    ? (f === 'win' ? '#2ecc71' : '#e74c3c')
                    : 'var(--chalk-dim)',
                }}
              >
                {f === 'win' ? `🏆 Wins (${state.player.wins})` : `💀 Losses (${state.player.losses})`}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 1rem 1.5rem' }}>
          {loading && (
            <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '2rem', fontSize: '0.85rem' }}>
              Loading match history…
            </div>
          )}
          {error && (
            <div style={{ color: '#e74c3c', padding: '1rem', fontSize: '0.82rem' }}>⚠ {error}</div>
          )}
          {!loading && !error && shown.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '2.5rem 1rem', fontSize: '0.85rem' }}>
              No {filter === 'win' ? 'wins' : 'losses'} recorded yet.
            </div>
          )}
          {!loading && !error && shown.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.5rem' }}>
              {shown.map((rec, i) => {
                const opPic = rec.opponent?.profilePictureUrl ?? PLACEHOLDER;
                const isWin = rec.result === 'win';
                return (
                  <button
                    key={`${rec.matchId}-${i}`}
                    onClick={() => { onClose(); onNavigateToTournament(rec.tournamentId); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      width: '100%', textAlign: 'left',
                      background: isWin ? 'rgba(46,204,113,0.06)' : 'rgba(231,76,60,0.06)',
                      border: `1px solid ${isWin ? 'rgba(46,204,113,0.18)' : 'rgba(231,76,60,0.18)'}`,
                      borderRadius: '10px',
                      padding: '0.7rem 0.85rem',
                      cursor: 'pointer',
                      transition: 'filter 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.filter = 'brightness(1.15)')}
                    onMouseLeave={e => (e.currentTarget.style.filter = '')}
                  >
                    {/* Opponent avatar */}
                    <img
                      src={opPic}
                      alt={rec.opponent?.name ?? 'Opponent'}
                      style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.1)', flexShrink: 0 }}
                    />

                    {/* Main info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isWin ? 'Beat' : 'Lost to'}{' '}
                        <span style={{ color: isWin ? '#2ecc71' : '#e74c3c' }}>
                          {rec.opponent?.name ?? 'Unknown'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', marginTop: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {rec.tournamentName} · {rec.roundName}
                      </div>
                      {rec.playedAt && (
                        <div style={{ fontSize: '0.66rem', color: 'var(--chalk-dim)', opacity: 0.7, marginTop: '0.1rem' }}>
                          {new Date(rec.playedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      )}
                    </div>

                    {/* Score + chevron */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                      {rec.playerScore !== null && rec.opponentScore !== null && (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.15rem', letterSpacing: '0.04em', lineHeight: 1 }}>
                            <span style={{ color: isWin ? '#2ecc71' : '#e74c3c' }}>{rec.playerScore}</span>
                            <span style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem', margin: '0 0.2rem' }}>–</span>
                            <span style={{ color: 'var(--chalk-dim)' }}>{rec.opponentScore}</span>
                          </div>
                        </div>
                      )}
                      <span style={{ color: 'var(--chalk-dim)', fontSize: '0.75rem', opacity: 0.6 }}>›</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PlayersAdminPage({ onNavigateToTournament }: { onNavigateToTournament: (id: string) => void }) {
  const { fetchAllPlayers, fetchPlayerMatches, updatePlayerGlobal, deletePlayerGlobal, mergePlayers } = useTournament();
  const { t } = useLang();

  const [players, setPlayers]               = useState<AllPlayer[] | null>(null);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [search, setSearch]                 = useState('');
  const [editing, setEditing]               = useState<EditingState | null>(null);
  const [saving, setSaving]                 = useState(false);
  const [handicapEdit, setHandicapEdit]     = useState<HandicapEdit | null>(null);
  const [savingHandicap, setSavingHandicap] = useState(false);
  const [nameEdit, setNameEdit]             = useState<NameEdit | null>(null);
  const [savingName, setSavingName]         = useState(false);
  const [deleteConfirm, setDeleteConfirm]   = useState<AllPlayer | null>(null);
  const [deleting, setDeleting]             = useState(false);
  // Drawer state lives in the URL (?player=<id>&filter=win|loss) rather than local state, so a
  // refresh doesn't lose which player's history is open. The player object itself is resolved
  // from the already-loaded `players` list by id — not refetched.
  const [searchParams, setSearchParams]     = useSearchParams();
  const [mergeConfirm, setMergeConfirm]     = useState<AllPlayer | null>(null);
  const [merging, setMerging]               = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchAllPlayers()
      .then(setPlayers)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load players'))
      .finally(() => setLoading(false));
  }, [fetchAllPlayers]);

  useEffect(() => { load(); }, [load]);

  // Live updates: a player added/edited/removed/merged from another device or the mobile app
  // shows up here immediately.
  useSocketEvents({ 'players:changed': load });

  // Reconnect resync — catch up on anything missed while disconnected.
  const { reconnectCount } = useConnectionStatus();
  useEffect(() => { if (reconnectCount > 0) load(); }, [reconnectCount, load]);

  const filtered = (players ?? []).filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const drawerPlayerId = searchParams.get('player');
  const drawerFilter = searchParams.get('filter') === 'loss' ? 'loss' : 'win';
  const drawerPlayer = drawerPlayerId ? (players ?? []).find(p => p._id === drawerPlayerId) ?? null : null;
  const drawer: DrawerState | null = drawerPlayer ? { player: drawerPlayer, filter: drawerFilter } : null;
  const openDrawer = (playerId: string, filter: 'win' | 'loss') => setSearchParams({ player: playerId, filter });
  const closeDrawer = () => setSearchParams(prev => { const next = new URLSearchParams(prev); next.delete('player'); next.delete('filter'); return next; });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file || !editing) return;
    resizeImageToDataUrl(file).then(dataUrl => {
      setEditing(prev => prev ? { ...prev, preview: dataUrl } : null);
    }).catch(() => {
      const reader = new FileReader();
      reader.onload = ev => setEditing(prev => prev ? { ...prev, preview: ev.target?.result as string } : null);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleSavePicture = async () => {
    if (!editing?.preview) return;
    setSaving(true); setError(null);
    try {
      await updatePlayerGlobal(editing.playerId, editing.preview);
      setEditing(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save photo');
    } finally { setSaving(false); }
  };

  const handleSaveHandicap = async () => {
    if (!handicapEdit) return;
    const val = Number(handicapEdit.value);
    if (isNaN(val) || val < 0) return;
    setSavingHandicap(true); setError(null);
    try {
      await updatePlayerGlobal(handicapEdit.playerId, undefined, val);
      setHandicapEdit(null);
      setPlayers(prev => prev
        ? prev.map(p => p._id === handicapEdit.playerId ? { ...p, handicap: val } : p)
        : prev
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save handicap');
    } finally { setSavingHandicap(false); }
  };

  const handleSaveName = async () => {
    if (!nameEdit || !nameEdit.value.trim()) return;
    const val = nameEdit.value.trim();
    setSavingName(true); setError(null);
    try {
      await updatePlayerGlobal(nameEdit.playerId, undefined, undefined, val);
      setNameEdit(null);
      setPlayers(prev => prev
        ? prev.map(p => p._id === nameEdit.playerId ? { ...p, name: val } : p)
        : prev
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save name');
    } finally { setSavingName(false); }
  };

  const handleDelete = async (player: AllPlayer) => {
    setDeleteConfirm(null);
    setDeleting(true); setError(null);
    try {
      await deletePlayerGlobal(player._id);
      setPlayers(prev => prev ? prev.filter(p => p._id !== player._id) : prev);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete player');
    } finally { setDeleting(false); }
  };

  const handleMerge = async (player: AllPlayer) => {
    setMergeConfirm(null);
    setMerging(true); setError(null);
    try {
      // The row's own _id first, so the surviving document is the one currently on screen.
      const ids = [player._id, ...player.mergeableIds.filter(id => id !== player._id)];
      await mergePlayers(ids);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to merge players');
    } finally { setMerging(false); }
  };

  const card: React.CSSProperties = {
    background: 'linear-gradient(135deg,rgba(255,255,255,0.05),rgba(255,255,255,0.02))',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px',
    padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.85rem',
  };

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: 'var(--page-pad)' }}>

      {drawer && (
        <MatchHistoryDrawer
          state={drawer}
          onClose={closeDrawer}
          fetchPlayerMatches={fetchPlayerMatches}
          onNavigateToTournament={onNavigateToTournament}
        />
      )}

      {deleteConfirm && (
        <ConfirmModal
          title={t.deletePlayerTitle}
          message={`Permanently delete ${deleteConfirm.name}? This will remove them from all tournaments and cannot be undone.`}
          confirmLabel={t.deletePlayer}
          cancelLabel={t.cancelLabel}
          danger
          onConfirm={() => handleDelete(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {mergeConfirm && (
        <ConfirmModal
          title="Merge Duplicate Players"
          message={`${mergeConfirm.mergeableIds.length} separate records were found for "${mergeConfirm.name}" (usually from being typed fresh instead of picked from the autocomplete). Merge them into one? Their combined win/loss record and match history will be kept; this cannot be undone.`}
          confirmLabel="🔗 Merge"
          cancelLabel={t.cancelLabel}
          onConfirm={() => handleMerge(mergeConfirm)}
          onCancel={() => setMergeConfirm(null)}
        />
      )}

      {/* Hidden shared file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.8rem,6vw,2.4rem)', color: 'var(--gold)', marginBottom: '0.3rem' }}>
        {t.playerManagement}
      </h2>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.82rem', marginBottom: '1.25rem' }}>
        {t.allPlayersDesc}
      </p>

      {/* Search */}
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search players…"
        style={{
          width: '100%', padding: '0.65rem 0.9rem', background: 'rgba(0,0,0,0.3)',
          border: '1px solid rgba(201,168,76,0.25)', borderRadius: '8px',
          color: 'var(--chalk)', fontSize: '0.9rem', outline: 'none',
          marginBottom: '1rem', boxSizing: 'border-box',
        }}
      />

      {error && (
        <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: '8px', padding: '0.65rem 1rem', color: '#e74c3c', fontSize: '0.82rem', marginBottom: '1rem' }}>
          ⚠ {error}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '2rem' }}>{t.loadingLabel}</div>
      )}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--chalk-dim)', padding: '3rem', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '2px dashed rgba(255,255,255,0.1)' }}>
          {search ? `${t.noPlayersMatching} "${search}"` : t.noPlayersFound}
        </div>
      )}

      {!loading && players !== null && (
        <p style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginBottom: '0.75rem' }}>
          {search
            ? t.playerCountMatch.replace('{n}', String(filtered.length)).replace('{s}', filtered.length !== 1 ? 's' : '').replace('{q}', search)
            : t.playerCountAll.replace('{n}', String(filtered.length)).replace('{s}', filtered.length !== 1 ? 's' : '')
          }
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {filtered.map(player => {
          const isEditing  = editing?.playerId === player._id;
          const displayPic = isEditing && editing.preview
            ? editing.preview
            : player.profilePictureUrl ?? PLACEHOLDER;
          const isEditingH = handicapEdit?.playerId === player._id;
          const isEditingN = nameEdit?.playerId === player._id;
          const played = player.wins + player.losses;

          return (
            <div key={player._id} style={card}>
              {/* Avatar */}
              <div
                onClick={() => {
                  setEditing({ playerId: player._id, preview: null });
                  setTimeout(() => fileInputRef.current?.click(), 50);
                }}
                style={{ position: 'relative', width: '52px', height: '52px', flexShrink: 0, cursor: 'pointer' }}
              >
                <img
                  src={displayPic}
                  alt={player.name}
                  style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', border: isEditing ? '2px solid var(--gold)' : '2px solid rgba(201,168,76,0.3)', display: 'block' }}
                />
                <div
                  style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
                >
                  <span style={{ fontSize: '1rem' }}>📷</span>
                </div>
              </div>

              {/* Name + tournament tags + W/L + handicap */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  {isEditingN ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flex: 1, minWidth: 0 }}>
                      <input
                        value={nameEdit.value}
                        onChange={e => setNameEdit({ playerId: player._id, value: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setNameEdit(null); }}
                        autoFocus
                        style={{ flex: 1, minWidth: 0, padding: '0.2rem 0.4rem', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(201,168,76,0.5)', borderRadius: '4px', color: 'var(--chalk)', fontSize: '0.9rem', outline: 'none' }}
                      />
                      <button onClick={handleSaveName} disabled={savingName} style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '4px', padding: '0.15rem 0.5rem', fontSize: '0.72rem', cursor: 'pointer', flexShrink: 0 }}>
                        {savingName ? '…' : '✓'}
                      </button>
                      <button onClick={() => setNameEdit(null)} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', padding: '0.15rem 0.35rem', fontSize: '0.72rem', cursor: 'pointer', flexShrink: 0 }}>✕</button>
                    </div>
                  ) : (
                    <div
                      onClick={() => setNameEdit({ playerId: player._id, value: player.name })}
                      title="Click to edit name"
                      style={{ fontWeight: 600, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                    >
                      {player.name} <span style={{ color: 'var(--chalk-dim)', fontSize: '0.65rem' }}>✎</span>
                    </div>
                  )}
                  {player.mergeableIds.length > 1 && (
                    <button
                      onClick={() => setMergeConfirm(player)}
                      disabled={merging}
                      title="This name is backed by several separate player records — merge them into one"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0, background: 'rgba(52,152,219,0.12)', color: '#5dade2', border: '1px solid rgba(52,152,219,0.35)', borderRadius: '4px', padding: '0.1rem 0.4rem', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer' }}
                    >
                      🔗 {player.mergeableIds.length} duplicates
                    </button>
                  )}
                </div>

                {/* Tournament badges */}
                <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.2rem', marginBottom: '0.2rem' }}>
                  {player.tournaments.map(tn => (
                    <span key={tn} style={{ fontSize: '0.6rem', background: 'rgba(201,168,76,0.12)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '3px', padding: '0.1rem 0.35rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                      {tn}
                    </span>
                  ))}
                </div>

                {/* Win / Loss record — clickable buttons */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', margin: '0.3rem 0 0.25rem' }}>
                  <button
                    onClick={() => openDrawer(player._id, 'win')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.28rem 0.7rem',
                      background: 'rgba(46,204,113,0.11)',
                      border: '1px solid rgba(46,204,113,0.3)',
                      borderRadius: '6px',
                      color: '#2ecc71',
                      fontSize: '0.75rem', fontWeight: 700,
                      letterSpacing: '0.03em',
                      cursor: 'pointer',
                      transition: 'background 0.15s, border-color 0.15s',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(46,204,113,0.2)'; e.currentTarget.style.borderColor = 'rgba(46,204,113,0.5)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(46,204,113,0.11)'; e.currentTarget.style.borderColor = 'rgba(46,204,113,0.3)'; }}
                  >
                    <span style={{ fontSize: '0.7rem' }}>🏆</span>
                    <span>{player.wins} win{player.wins !== 1 ? 's' : ''}</span>
                  </button>
                  <button
                    onClick={() => openDrawer(player._id, 'loss')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.28rem 0.7rem',
                      background: 'rgba(231,76,60,0.1)',
                      border: '1px solid rgba(231,76,60,0.28)',
                      borderRadius: '6px',
                      color: '#e74c3c',
                      fontSize: '0.75rem', fontWeight: 700,
                      letterSpacing: '0.03em',
                      cursor: 'pointer',
                      transition: 'background 0.15s, border-color 0.15s',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(231,76,60,0.2)'; e.currentTarget.style.borderColor = 'rgba(231,76,60,0.5)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(231,76,60,0.1)'; e.currentTarget.style.borderColor = 'rgba(231,76,60,0.28)'; }}
                  >
                    <span style={{ fontSize: '0.7rem' }}>💀</span>
                    <span>{player.losses} loss{player.losses !== 1 ? 'es' : ''}</span>
                  </button>
                  {played > 0 && (
                    <span style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', whiteSpace: 'nowrap' }}>
                      {Math.round((player.wins / played) * 100)}%
                    </span>
                  )}
                  {player.avgShotTimeSeconds !== null && (
                    <span style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', whiteSpace: 'nowrap' }}>
                      ⏱ Avg shot: {Math.round(player.avgShotTimeSeconds)}s
                    </span>
                  )}
                </div>

                {/* Handicap inline editor */}
                {isEditingH ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.2rem' }}>
                    <input
                      type="number" min="0" max="147"
                      value={handicapEdit.value}
                      onChange={e => setHandicapEdit({ playerId: player._id, value: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveHandicap(); if (e.key === 'Escape') setHandicapEdit(null); }}
                      autoFocus
                      style={{ width: '60px', padding: '0.2rem 0.4rem', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(201,168,76,0.5)', borderRadius: '4px', color: 'var(--gold)', fontSize: '0.82rem', outline: 'none', textAlign: 'center' }}
                    />
                    <button onClick={handleSaveHandicap} disabled={savingHandicap} style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '4px', padding: '0.15rem 0.5rem', fontSize: '0.72rem', cursor: 'pointer' }}>
                      {savingHandicap ? '…' : '✓'}
                    </button>
                    <button onClick={() => setHandicapEdit(null)} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', padding: '0.15rem 0.35rem', fontSize: '0.72rem', cursor: 'pointer' }}>✕</button>
                  </div>
                ) : (
                  <div
                    onClick={() => setHandicapEdit({ playerId: player._id, value: String(player.handicap) })}
                    style={{ fontSize: '0.72rem', color: 'var(--gold)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.1rem' }}
                    title={t.clickToEditHandicap}
                  >
                    {t.handicapValue}: <strong>{player.handicap}</strong> <span style={{ color: 'var(--chalk-dim)', fontSize: '0.65rem' }}>✎</span>
                  </div>
                )}

                {isEditing && editing?.preview && (
                  <div style={{ fontSize: '0.68rem', color: 'var(--gold)', marginTop: '0.15rem' }}>{t.newPhotoSelected}</div>
                )}
              </div>

              {/* Photo save/cancel or delete button */}
              {isEditing && editing?.preview ? (
                <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                  <button
                    onClick={handleSavePicture}
                    disabled={saving}
                    style={{ background: 'linear-gradient(135deg,var(--gold),var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', minHeight: '36px', opacity: saving ? 0.6 : 1 }}
                  >
                    {saving ? '…' : '✓ Save'}
                  </button>
                  <button
                    onClick={() => setEditing(null)}
                    style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', padding: '0.4rem 0.7rem', fontSize: '0.78rem', cursor: 'pointer', minHeight: '36px' }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setDeleteConfirm(player)}
                  disabled={deleting}
                  title={t.deletePlayer}
                  style={{ background: 'rgba(192,57,43,0.1)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.25)', borderRadius: '6px', padding: '0.4rem 0.7rem', fontSize: '0.78rem', cursor: 'pointer', minHeight: '36px', flexShrink: 0 }}
                >
                  🗑
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
